import { useState, useEffect, useContext, useMemo } from 'react';
import { useGet } from '@/query/fetchHooks';
import { LessonEditFroala } from './LessonEditFroala';
import { AuthContext } from '@/components/Contexts/AuthContext';
import {
  Button,
  Spinner,
  TEOLoader,
  Dropdown,
  Modal,
  InputText,
} from '@teo/components';
import { LessonView } from './LessonView';
import { useTranslation } from 'react-i18next';
import { initFroala } from '@/components/Froala/froala.js';
import ExtraInfo from '@/components/ExtraInfo/ExtraInfo';
import { Cross, MoreVertical } from '@teo/components/icons';
import { LessonEditTitleBar } from './LessonEditTitleBar';
import { buildTreeFlat, newUid } from '@/util';
import SubtitleEditorPage from './SubtitleEditorPage';
import { AddImageToLibrary } from './AddImageToLibrary';
import { LibraryModal } from '@/components/LibraryModal/LibraryModal';
import { axios } from '@/axios';
import { config } from '@/config';
import DeleteModalLesson from './DeleteModal/DeleteModalLesson';

export const LessonEdit = ({
  node,
  setSaveFn,
  resetAutoSave,
  publicationName,
  publication,
  setTree,
  prevTree,
  setAddLesson,
  setLessons,
  lessons,
  directories,
  setActiveNode,
  setParentNode,
  setMobileSidebar,
  setExtraInfoEdit,
  extraInfoEdit,
  updateLesInfo,
  setIsFroalaLoaded,
  isFroalaLoaded,
  hints,
  newPage,
  touchUpdatedAt,
}: {
  node: any;
  setSaveFn: any;
  resetAutoSave: any;
  setTree: any;
  publicationName: any;
  publication: any;
  prevTree: any;
  setAddLesson: any;
  setLessons: any;
  lessons: any;
  directories?: any;
  setActiveNode: any;
  setParentNode: any;
  setMobileSidebar: any;
  setExtraInfoEdit: any;
  extraInfoEdit: any;
  updateLesInfo?: any;
  setIsFroalaLoaded?: any;
  isFroalaLoaded?: any;
  hints?: any;
  newPage?: any;
  touchUpdatedAt?: () => void;
}) => {
  const { t } = useTranslation();
  const { auth } = useContext(AuthContext);
  const authUser = auth?.user;
  const organisationId = authUser?.activeOrganisationId;
  const [content, setContent] = useState<any>(null);
  const [loadLessons, setLoadLessons] = useState<any>(true);
  const [isLesson, setIsLesson] = useState<any>(true);
  const [showMessage, setShowMessage] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const [showExtraInfo, setShowExtraInfo] = useState<boolean>(false);
  const [extraInfoLessons, setExtraInfoLessons] = useState<any>([]);
  const [isAssetPath, setIsAssetPath] = useState(null);
  const [isSubtitleEditorOpen, setIsSubtitleEditorOpen] = useState(false);
  const [subtitleEditorData, setSubtitleEditorData] = useState<any>(null);
  const [isOpenAddLibraryModal, setIsOpenAddLibraryModal] = useState(false);
  const [isOpenLibraryModal, setIsOpenLibraryModal] = useState(false);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [isOpenAltTextModal, setIsOpenAltTextModal] = useState(false);
  const [assetPathAlt, setAssetPathAlt] = useState(null);
  const [exists, setExists] = useState(false);

  const url = node?.data?.assetPath;
  const isAmazonAws = url?.includes('amazonaws.com');
  const isCloudFront = url?.includes('cloudfront.net');

  let uid;
  let imgName;
  let imgPrev;
  if (isAmazonAws) {
    const fileName = url.split('/').pop();
    imgPrev = fileName.substring(fileName.indexOf('-') + 1);
    imgName = imgPrev.substring(0, imgPrev.lastIndexOf('.'));
    const decodedURL = decodeURIComponent(url);
    const fileTitle = decodedURL.split('/').pop();
    uid = fileTitle?.split('-')[0];
  }

  if (isCloudFront) {
    const fileName = url.split('/').pop();
    imgPrev = fileName.substring(fileName.indexOf('-') + 1);
    imgName = imgPrev.substring(0, imgPrev.lastIndexOf('.'));
  }

  const { refetch } = useGet(`/imagelibraries`, {
    enabled: !!isAssetPath,
    organisationId: organisationId,
    imageUid: isAmazonAws ? uid : isCloudFront ? imgName : uid,
    onSuccess: (result: any) => {
      if (result?.data?.length > 0) {
        setExists(true);
      } else {
        setExists(false);
      }
    },
  });

  useEffect(() => {
    if (node) {
      setIsLoaded(false);
      setIsLesson(!!node?.data?.lessonId);
      setIsAssetPath(node?.data?.assetPath ? node?.data?.assetPath : false);
      setAssetPathAlt(
        node?.data?.assetAltPath ? node?.data?.assetAltPath : false
      );
      setLoadLessons(true);
      setIsFroalaLoaded(null);
    }
  }, [node]);

  useEffect(() => {
    const handleOpenSubtitleEditor = (event: Event) => {
      const detail = (event as CustomEvent).detail;

      setSubtitleEditorData(detail || {});
    };

    window.addEventListener(
      'teo:open-subtitle-editor',
      handleOpenSubtitleEditor
    );

    return () => {
      window.removeEventListener(
        'teo:open-subtitle-editor',
        handleOpenSubtitleEditor
      );
    };
  }, []);

  useEffect(() => {
    if (!isLesson && isAssetPath) {
      setLoadLessons(false);
    }
  }, [isLesson, isAssetPath, node]);

  useEffect(() => {
    if (lessons) {
      setExtraInfoLessons(
        lessons?.filter(
          (les: any) =>
            les?.rootLessonId === node?.data?.lessonId &&
            les?.path?.includes(node?.data?.path)
        )
      );
    }
  }, [lessons, node]);

  const contentLinkTargets = useMemo(
    () => ({
      lessons: (
        (directories
          ? (
              buildTreeFlat(
                (lessons || []).filter(
                  (les: any) => !les?.rootLessonId && !les?.rootDirectoryId
                ),
                directories
              ) as any
            )[0].map((x: any) => x.data)
          : lessons) || []
      ).filter(
        (les: any) =>
          !les?.rootLessonId &&
          !les?.rootDirectoryId &&
          (les?.lessonId || les?.assetId) &&
          les?.id !== node?.data?.id
      ),
      extraInfo: [
        ...(extraInfoLessons || []).map((les: any) => ({
          ...les,
          _group: node?.data?.name,
        })),
        ...(directories || [])
          .filter((dir: any) => {
            const parts = (node?.data?.path || '')
              .split('/')
              .filter((part: string) => part !== '');
            parts.pop();
            return (
              dir?.path !== '/' &&
              parts.some((part: string) => dir?.path?.endsWith(part))
            );
          })
          .sort((a: any, b: any) => b?.path?.length - a?.path?.length)
          .flatMap((dir: any) =>
            (lessons || [])
              .filter((les: any) => les?.rootDirectoryId === dir?.id)
              .map((les: any) => ({ ...les, _group: dir?.name }))
          ),
        ...(lessons || [])
          .filter(
            (les: any) =>
              les?.rootDirectoryId &&
              les?.rootDirectoryId ===
                (directories || []).find((dir: any) => dir?.path === '/')?.id
          )
          .map((les: any) => ({
            ...les,
            _group: t('widgets.content_link.group_course'),
          })),
      ],
    }),
    [lessons, directories, extraInfoLessons, node?.data?.id, node?.data?.path]
  );

  const lessonResult = useGet<any>(node?.data?.getData, {
    enabled: !!node?.data?.getData,
    onSuccess: async (result: any) => {
      setContent(result?.data || '<p></p>');
      await initFroala();
      setIsLoaded(true);
      setLoadLessons(false);
    },
  });

  const handleChangeImg = async (url: any) => {
    await axios.put(`${config.backend}/asset/${node?.data?.assetId}`, {
      id: node?.data?.assetId,
      assetPath: url,
    });
    setIsAssetPath(url);
    setLessons((prev: any) =>
      prev?.map((el: any) =>
        el.id === node?.data?.id ? { ...el, assetPath: url } : el
      )
    );
  };

  const handleDelete = async () => {
    await axios.delete(`${config.backend}/use/${node?.data?.usesId}`);
    setLessons((prev: any) => prev?.filter((der: any) => der?.id !== node?.id));
    setActiveNode(null);
  };

  const handleChangeAlt = async () => {
    await axios.put(`${config.backend}/asset/${node?.data?.assetId}`, {
      id: node?.data?.assetId,
      assetAltPath: assetPathAlt ? assetPathAlt : null,
    });
    setLessons((prev: any) =>
      prev?.map((el: any) =>
        el.id === node?.data?.id
          ? { ...el, assetAltPath: assetPathAlt ? assetPathAlt : null }
          : el
      )
    );
    setIsOpenAltTextModal(false);
  };

  // console.log(node);

  return (
    <>
      {loadLessons && (isLesson || isAssetPath) && (
        <div className="m-auto flex min-h-[calc(100vh_-_150px)] p-10">
          <div className="m-auto">
            <TEOLoader
              loaderSrc="/images/TEOloader.gif"
              className="w-[110px]"
            />
          </div>
        </div>
      )}

      {!extraInfoEdit && !loadLessons && (
        <LessonEditTitleBar
          key={updateLesInfo}
          node={node}
          publicationName={publicationName}
          prevTree={prevTree}
          setLessons={setLessons}
          setShowExtraInfo={setShowExtraInfo}
          extraInfoLessons={extraInfoLessons}
          setActiveNode={setActiveNode}
          hints={hints}
          newPage={newPage}
        />
      )}

      {!loadLessons &&
        isLesson &&
        (node?.data?.canEdit ? (
          <div className="px-3 pt-3">
            <LessonEditFroala
              key={node?.data?.lessonId}
              isLoading={!isLoaded}
              newContent={content}
              setSaveFn={(fn: any) => setSaveFn(fn)}
              resetAutoSave={resetAutoSave}
              node={node}
              isLesson={isLesson}
              setIsFroalaLoaded={setIsFroalaLoaded}
              touchUpdatedAt={touchUpdatedAt}
              contentLinkTargets={contentLinkTargets}
            />
          </div>
        ) : (
          <div className="flex">
            <div className="mx-auto max-w-2xl">
              {showMessage && (
                <div className="relative mb-5 rounded-lg bg-[#f8f8f9] p-4 pr-6">
                  <div
                    className="absolute right-0 top-0 cursor-pointer p-2"
                    onClick={() => setShowMessage(false)}
                  >
                    <Cross className="w-5" />
                  </div>
                  <h3 className="mb-3 text-base">
                    {t('pages.create_edit_course.linking_message.title')}
                  </h3>
                  <p className="mb-2 text-sm text-grey-08">
                    {t('pages.create_edit_course.linking_message.message')}
                  </p>
                  {/* <div>
                <Button variant="outline" onClick={() => setShowMessage(false)}>
                  {t('pages.create_edit_course.linking_message.button')}
                </Button>
              </div> */}
                </div>
              )}

              <LessonView node={node?.data}></LessonView>
            </div>
          </div>
        ))}

      {!loadLessons && !isLesson && isAssetPath && (
        <div className="relative mx-auto flex w-full max-w-[744px] items-start p-3">
          {node?.data?.assetType == 'image' ? (
            <>
              <img
                src={isAssetPath}
                alt={assetPathAlt ? assetPathAlt : node?.data?.assetName}
                className="mx-auto max-h-[100vh] w-full object-contain"
              />
              <div className="absolute right-6 top-6 ">
                <Dropdown>
                  <Dropdown.Trigger>
                    <div className="rounded-[4px] border bg-grey-01 py-1 px-1 hover:bg-grey-02">
                      <MoreVertical className="h-5 w-5 text-grey-08" />
                    </div>
                  </Dropdown.Trigger>
                  <Dropdown.Content
                    style={{
                      top: '20px',
                      padding: '4px 0',
                    }}
                  >
                    <Dropdown.Actions>
                      <Dropdown.Button
                        style={{ height: '40px', padding: '0 8px' }}
                        onClick={async () => {
                          setIsOpenLibraryModal(true);
                        }}
                      >
                        <p className="flex items-center gap-2 text-sm">
                          <i className="far fa-exchange-alt text-[17px]"></i>
                          {t(
                            'widgets.toolbar_button.image_settings.replace_image'
                          )}
                        </p>
                      </Dropdown.Button>

                      <Dropdown.Button
                        style={{ height: '40px', padding: '0 8px' }}
                        onClick={async () => {
                          setIsOpenAddLibraryModal(true);
                        }}
                      >
                        <p className="flex items-center gap-2 text-sm">
                          <i className="fal fa-books text-[16px] !text-[#fdbe06]"></i>
                          {exists
                            ? t(
                                'widgets.toolbar_button.image_settings.edit_to_library'
                              )
                            : t(
                                'widgets.toolbar_button.image_settings.add_to_library'
                              )}
                        </p>
                      </Dropdown.Button>
                      <Dropdown.Button
                        style={{ height: '40px', padding: '0 8px' }}
                        onClick={async () => {
                          setIsOpenAltTextModal(true);
                        }}
                      >
                        <p className="flex items-center gap-2 text-sm">
                          <img
                            src="/images/icons/alt_text.svg"
                            className="!w-5"
                          />
                          {t('widgets.toolbar_button.image_settings.alt_text')}
                        </p>
                      </Dropdown.Button>

                      <Dropdown.Button
                        style={{ height: '40px', padding: '0 8px' }}
                        onClick={async () => {
                          setIsOpenDeleteModal(true);
                        }}
                      >
                        <p className="flex items-center gap-2 text-sm">
                          <img
                            src="/images/icons/trash_icon_red.svg"
                            className="!w-4"
                          />
                          {t('pages.create_edit_course.remove')}
                        </p>
                      </Dropdown.Button>
                    </Dropdown.Actions>
                  </Dropdown.Content>
                </Dropdown>
              </div>
            </>
          ) : (
            <>
              {node?.data?.assetType == 'video' ? (
                <div id={`${newUid(20)}_embed`} className="mx-auto">
                  <iframe
                    id={`${newUid(20)}_placeholder`}
                    width="640"
                    height="360"
                    className=""
                    src={node?.data?.assetPath}
                    allowFullScreen
                  ></iframe>
                </div>
              ) : (
                <div
                  id={`${newUid(20)}_embed`}
                  className="mx-auto"
                  dangerouslySetInnerHTML={{
                    __html: node?.data?.assetPath,
                  }}
                />
              )}
            </>
          )}
        </div>
      )}

      {showExtraInfo && (
        <ExtraInfo
          setShowExtraInfo={setShowExtraInfo}
          activeNode={node}
          node={node}
          publication={publication}
          prevTree={prevTree}
          setTree={setTree}
          setAddLesson={setAddLesson}
          setLessons={setLessons}
          nameItem={node?.text}
          extraInfoLessons={extraInfoLessons}
          setActiveNode={setActiveNode}
          setParentNode={setParentNode}
          setMobileSidebar={setMobileSidebar}
          setExtraInfoEdit={setExtraInfoEdit}
          extraInfoEdit={extraInfoEdit}
          isFroalaLoaded={isFroalaLoaded}
        />
      )}

      <SubtitleEditorPage
        isOpen={!!subtitleEditorData}
        videoUrl={subtitleEditorData?.videoUrl}
        lang={subtitleEditorData?.lang}
        iframeEl={subtitleEditorData?.iframeEl}
        editor={subtitleEditorData?.editor}
        onClose={() => setSubtitleEditorData(null)}
      />
      {isOpenAddLibraryModal && (
        <AddImageToLibrary
          imageUrl={node?.data?.assetPath}
          imageName={node?.data?.assetName}
          isOpenAddLibraryModal={isOpenAddLibraryModal}
          setIsOpenAddLibraryModal={setIsOpenAddLibraryModal}
          refetchLibrary={refetch}
        />
      )}

      {isOpenLibraryModal && (
        <LibraryModal
          setIsOpenLibraryModal={setIsOpenLibraryModal}
          isOpenLibraryModal={isOpenLibraryModal}
          lessonPage={false}
          setUploadFile={(url: any) => handleChangeImg(url)}
          onSelectImage={(url: any) => handleChangeImg(url)}
        />
      )}
      {isOpenDeleteModal && (
        <DeleteModalLesson
          title={t('pages.create_edit_course.delete_modal.title_asset')}
          heading={t('pages.create_edit_course.delete_modal.heading_asset')}
          text={t('pages.create_edit_course.delete_modal.subtitle_assey')}
          les={node?.data}
          setIsOpenDeleteModal={setIsOpenDeleteModal}
          closeModal={() => setIsOpenDeleteModal(false)}
          handleDelete={handleDelete}
        />
      )}
      {isOpenAltTextModal && (
        <Modal isOpen={true} className="max-w-lg">
          <div
            className=""
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            <Modal.Header onClose={() => setIsOpenAltTextModal(false)}>
              <h2 className="text-sm font-semibold">
                {t('widgets.toolbar_button.image_settings.alternative_text')}
              </h2>
            </Modal.Header>

            <Modal.Body>
              <div className="flex flex-col gap-6">
                <div>
                  <InputText
                    value={assetPathAlt ? assetPathAlt : ''}
                    onChange={(ev: any) => setAssetPathAlt(ev.target.value)}
                    placeholder={t(
                      'widgets.toolbar_button.image_settings.pl_alternative_text'
                    )}
                  />
                </div>
                <div className="flex justify-between">
                  <Button
                    variant="ghost"
                    onClick={() => setIsOpenAltTextModal(false)}
                  >
                    {t('button.cancel')}
                  </Button>
                  <Button onClick={() => handleChangeAlt()}>
                    {t('button.save')}
                  </Button>
                </div>
              </div>
            </Modal.Body>
          </div>
        </Modal>
      )}
    </>
  );
};
